/*!
 * bs5-datetimepicker — a lightweight jQuery date + time picker for Bootstrap 5
 * No dependencies beyond jQuery and Bootstrap 5 CSS.
 * Works inside Bootstrap modals/dialogs (panel is appended to <body> with a
 * high z-index and positioned against the input, so modal overflow never clips it).
 *
 * Usage:
 *   $('#myInput').bs5DateTimePicker({ ...options });
 *   $('#myInput').bs5DateTimePicker('getDate');   // -> Date | null
 *   $('#myInput').bs5DateTimePicker('setDate', new Date());
 *   $('#myInput').bs5DateTimePicker('destroy');
 *   $('#myInput').data('bs5dtp').setNowOverride(fn); // fn() -> Date, substitute for minDate/maxDate: 'now'
 *
 * Events (namespaced .bs5dtp): 'change.bs5dtp', 'show.bs5dtp', 'hide.bs5dtp'
 */
(function ($) {
  'use strict';

  var DEFAULTS = {
    format: 'YYYY-MM-DD HH:mm', // tokens: YYYY MM DD HH mm ss, hh (12h), A (AM/PM)
    time: true,                 // show the time column
    seconds: false,             // show seconds in the time column
    use24Hour: true,            // 24h clock vs 12h with AM/PM
    minuteStep: 5,              // minute increment for the stepper
    weekStart: 0,               // 0 = Sunday, 1 = Monday
    minDate: null,              // Date | parseable string | null
    maxDate: null,              // Date | parseable string | null
    todayButton: true,
    clearButton: true,
    closeOnSelect: false,       // with time on, keep open until "Apply"; date-only closes
    autoApply: false,           // if true, no Apply button, applies on each change; opt in per-field via data-auto-apply="true"
    appendTo: 'body',           // where the panel attaches (kept on body for modals)
    zIndexBoost: 0,             // extra z-index added on top of the computed base
    monthNames: ['January','February','March','April','May','June','July',
                 'August','September','October','November','December'],
    dayNamesMin: ['Su','Mo','Tu','We','Th','Fr','Sa'],
    todayText: 'Today',
    clearText: 'Clear',
    applyText: 'Apply'
  };

  // ---- tiny date helpers (no external lib) ---------------------------------
  function pad(n, len) { n = String(n); while (n.length < (len || 2)) n = '0' + n; return n; }

  function toDate(v) {
    if (v == null || v === '') return null;
    if (v instanceof Date) return isNaN(v) ? null : v;
    var d = new Date(v);
    return isNaN(d) ? null : d;
  }

  function startOfDay(d) { var x = new Date(d); x.setHours(0, 0, 0, 0); return x; }

  function sameDay(a, b) {
    return a && b && a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  function formatDate(d, fmt, opt) {
    if (!d) return '';
    var h24 = d.getHours();
    var h12 = h24 % 12; if (h12 === 0) h12 = 12;
    var map = {
      YYYY: d.getFullYear(),
      MM: pad(d.getMonth() + 1),
      DD: pad(d.getDate()),
      HH: pad(h24),
      hh: pad(h12),
      mm: pad(d.getMinutes()),
      ss: pad(d.getSeconds()),
      A: h24 < 12 ? 'AM' : 'PM'
    };
    return fmt.replace(/YYYY|MM|DD|HH|hh|mm|ss|A/g, function (t) { return map[t]; });
  }

  // Parse a value the user typed back into a Date. Lenient: relies on native
  // Date parsing first, then a fallback for the default-style format.
  function parseInput(str) {
    if (!str) return null;
    var native = new Date(str);
    if (!isNaN(native)) return native;
    var m = String(str).match(/(\d{4})\D(\d{1,2})\D(\d{1,2})(?:\D+(\d{1,2})\D(\d{1,2})(?:\D(\d{1,2}))?)?/);
    if (m) {
      return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
    }
    return null;
  }

  // ---- the picker instance --------------------------------------------------
  function Picker($input, options) {
    this.$input = $input;
    this.opt = $.extend(true, {}, DEFAULTS, options || {});
    // minDate/maxDate: 'now' is a live sentinel (re-evaluated on every read
    // via _minDate()/_maxDate()) rather than a Date frozen at construction
    // time, so a picker left open for a while still enforces the actual
    // current moment. Any other value is parsed once, as before.
    this.min = this.opt.minDate === 'now' ? null : toDate(this.opt.minDate);
    this.max = this.opt.maxDate === 'now' ? null : toDate(this.opt.maxDate);
    // 'now' normally means the browser's own clock; setNowOverride() lets
    // calling code (e.g. a Time Zone select bound to this field) substitute
    // "the current wall-clock moment in some other zone" instead, so the
    // floor/ceiling agrees with how the submitted value will be interpreted
    // server-side rather than with the browser's local zone.
    this._nowOverride = null;

    var initial = parseInput($input.val());
    this.value = initial ? new Date(initial) : null;
    this.view = startOfDay(this.value || new Date()); // month being displayed
    this.draft = this.value ? new Date(this.value) : null; // pending selection

    this.id = 'bs5dtp-' + Math.random().toString(36).slice(2, 9);
    this.open = false;
    this._build();
    this._bind();
  }

  Picker.prototype._build = function () {
    this.$panel = $(
      '<div class="bs5dtp card shadow" role="dialog" aria-label="Choose date and time">' +
        '<div class="card-body p-2 d-flex gap-2">' +
          '<div class="bs5dtp-cal"></div>' +
          (this.opt.time ? '<div class="bs5dtp-time border-start ps-2"></div>' : '') +
        '</div>' +
        '<div class="bs5dtp-foot d-flex justify-content-between align-items-center gap-2 px-2 pb-2"></div>' +
      '</div>'
    ).attr('id', this.id).hide();
    this._renderFoot();
  };

  Picker.prototype._renderFoot = function () {
    var $f = this.$panel.find('.bs5dtp-foot');
    var left = '<div class="d-flex gap-1">';
    if (this.opt.todayButton) left += '<button type="button" class="btn btn-sm btn-link text-decoration-none p-0 px-1 bs5dtp-today">' + this.opt.todayText + '</button>';
    if (this.opt.clearButton) left += '<button type="button" class="btn btn-sm btn-link text-decoration-none p-0 px-1 text-secondary portal-secondary bs5dtp-clear">' + this.opt.clearText + '</button>';
    left += '</div>';
    var right = this.opt.autoApply ? '' :
      '<button type="button" class="btn btn-sm btn-primary bs5dtp-apply">' + this.opt.applyText + '</button>';
    $f.html(left + right);
  };

  Picker.prototype._minDate = function () {
    if (this.opt.minDate !== 'now') return this.min;
    return this._nowOverride ? this._nowOverride() : new Date();
  };

  // See the comment on this._nowOverride above. Takes a function (not a
  // Date) so the override stays as live as the plain 'now' sentinel it
  // replaces -- called fresh on every _minDate()/_maxDate() read (typing,
  // blur, day click, Apply) rather than pinned to whatever the moment was
  // when the Time Zone select last changed.
  Picker.prototype.setNowOverride = function (fn) {
    this._nowOverride = (typeof fn === 'function') ? fn : null;
    // The floor/ceiling may have just moved (e.g. Time Zone changed): if
    // that leaves the already-selected value out of range, re-clamp and
    // sync the field now, rather than letting a now-invalid value sit
    // silently until the server rejects it on submit. Only apply when the
    // clamp actually changes something, so switching zones (or the initial
    // call on a fresh dialog) doesn't fire a spurious 'change' when the
    // current value is still perfectly valid.
    if (this.draft) {
      var clamped = this._clampToRange(this.draft);
      if (clamped.getTime() !== this.draft.getTime()) this._apply(false);
    }
    if (this.open) this._render();
  };

  Picker.prototype._maxDate = function () {
    if (this.opt.maxDate !== 'now') return this.max;
    return this._nowOverride ? this._nowOverride() : new Date();
  };

  Picker.prototype._inRange = function (d) {
    var min = this._minDate(), max = this._maxDate();
    if (min && startOfDay(d) < startOfDay(min)) return false;
    if (max && startOfDay(d) > startOfDay(max)) return false;
    return true;
  };

  // Pull a draft datetime back inside [min, max] at the full datetime
  // granularity (not just the day) -- e.g. picking today's date but leaving
  // an earlier time selected should snap forward to "now", not just accept
  // any time on an allowed day.
  Picker.prototype._clampToRange = function (d) {
    if (!d) return d;
    var r = new Date(d), min = this._minDate(), max = this._maxDate();
    if (min && r < min) r = new Date(min);
    if (max && r > max) r = new Date(max);
    return r;
  };

  Picker.prototype._renderCalendar = function () {
    var o = this.opt, view = this.view;
    var $cal = this.$panel.find('.bs5dtp-cal');
    // Rebuilding the grid below destroys any focused day cell or prev/next
    // month button; capture it here so every caller (day click, Today,
    // steppers, month nav, ...) gets it back instead of each needing to
    // remember to reattach focus itself.
    var focused = document.activeElement;
    var $focused = ($cal[0] && $cal[0].contains(focused)) ? $(focused) : $();
    var restoreSelector = $focused.is('.bs5dtp-day')
      ? '.bs5dtp-day[data-date="' + $focused.data('date') + '"]'
      : $focused.is('.bs5dtp-prev') ? '.bs5dtp-prev'
      : $focused.is('.bs5dtp-next') ? '.bs5dtp-next'
      : null;
    var year = view.getFullYear(), month = view.getMonth();
    var first = new Date(year, month, 1);
    var startOffset = (first.getDay() - o.weekStart + 7) % 7;
    var gridStart = new Date(year, month, 1 - startOffset);

    var head =
      '<div class="d-flex justify-content-between align-items-center mb-1">' +
        '<button type="button" class="btn btn-sm btn-light bs5dtp-prev" aria-label="Previous month">&lsaquo;</button>' +
        '<div class="fw-semibold bs5dtp-title small">' + o.monthNames[month] + ' ' + year + '</div>' +
        '<button type="button" class="btn btn-sm btn-light bs5dtp-next" aria-label="Next month">&rsaquo;</button>' +
      '</div>';

    var dow = '<div class="bs5dtp-row text-secondary portal-secondary small fw-semibold">';
    for (var i = 0; i < 7; i++) dow += '<div class="bs5dtp-cell">' + o.dayNamesMin[(o.weekStart + i) % 7] + '</div>';
    dow += '</div>';

    var today = new Date();
    var sel = this.draft;
    var body = '<div class="bs5dtp-grid">';
    var d = new Date(gridStart);
    for (var w = 0; w < 6; w++) {
      body += '<div class="bs5dtp-row">';
      for (var c = 0; c < 7; c++) {
        var cls = 'bs5dtp-cell bs5dtp-day';
        if (d.getMonth() !== month) cls += ' text-secondary portal-secondary bs5dtp-other';
        if (sameDay(d, today)) cls += ' bs5dtp-is-today';
        if (sel && sameDay(d, sel)) cls += ' bs5dtp-selected';
        var disabled = !this._inRange(d);
        if (disabled) cls += ' bs5dtp-disabled';
        body += '<div class="' + cls + '" data-date="' + formatDate(d, 'YYYY-MM-DD') + '"' +
          (disabled ? ' aria-disabled="true"' : ' tabindex="0" role="button"') + '>' + d.getDate() + '</div>';
        d.setDate(d.getDate() + 1);
      }
      body += '</div>';
    }
    body += '</div>';
    $cal.html(head + dow + body);
    if (restoreSelector) $cal.find(restoreSelector).trigger('focus');
  };

  Picker.prototype._renderTime = function () {
    if (!this.opt.time) return;
    var o = this.opt;
    var base = this.draft || new Date();
    var h24 = base.getHours();
    var mins = base.getMinutes();
    var secs = base.getSeconds();
    var disp = o.use24Hour ? pad(h24) : pad(((h24 % 12) || 12));
    var ampm = h24 < 12 ? 'AM' : 'PM';

    // Update in place once built — a full rebuild on blur/focusout would
    // detach the element mid focus-change (e.g. Tab between H/M/S).
    var $wrap = this.$panel.find('.bs5dtp-timewrap');
    if ($wrap.length) {
      var active = document.activeElement;
      // Leave the focused field's raw text alone only while it already
      // parses to the value we're about to write, so a mid-typed "1" isn't
      // padded to "01" under the caret -- but a stepper or AM/PM change to
      // that same unit (mousedown on those buttons is prevented from
      // stealing focus, so the field stays "focused" through the click)
      // still shows up immediately.
      var set = function ($el, val) {
        if ($el[0] === active && parseInt($el.val(), 10) === parseInt(val, 10)) return;
        $el.val(val);
      };
      set($wrap.find('.bs5dtp-num[data-unit="h"]'), disp);
      set($wrap.find('.bs5dtp-num[data-unit="m"]'), pad(mins));
      if (o.seconds) set($wrap.find('.bs5dtp-num[data-unit="s"]'), pad(secs));
      if (!o.use24Hour) $wrap.find('.bs5dtp-ampm').text(ampm);
      return;
    }

    var html = '<div class="bs5dtp-timewrap text-center">';
    html += '<div class="small text-secondary portal-secondary fw-semibold mb-1">Time</div>';
    html += '<div class="d-flex align-items-center justify-content-center gap-1">';
    html += unit('h', disp);
    html += '<div class="fw-bold">:</div>';
    html += unit('m', pad(mins));
    if (o.seconds) { html += '<div class="fw-bold">:</div>' + unit('s', pad(secs)); }
    if (!o.use24Hour) {
      html += '<button type="button" class="btn btn-sm btn-outline-secondary ms-1 bs5dtp-ampm" style="min-width:42px">' + ampm + '</button>';
    }
    html += '</div></div>';
    this.$panel.find('.bs5dtp-time').html(html);

    function unit(name, val) {
      return '<div class="bs5dtp-stepper d-flex flex-column align-items-center">' +
        '<button type="button" class="btn btn-sm btn-light bs5dtp-step" data-unit="' + name + '" data-dir="1">&plus;</button>' +
        '<input type="text" inputmode="numeric" class="form-control form-control-sm text-center bs5dtp-num" data-unit="' + name + '" value="' + val + '" style="width:48px">' +
        '<button type="button" class="btn btn-sm btn-light bs5dtp-step" data-unit="' + name + '" data-dir="-1">&minus;</button>' +
      '</div>';
    }
  };

  Picker.prototype._render = function () {
    this._renderCalendar();
    this._renderTime();
  };

  // ensure there's a draft date when the user starts interacting time-first
  Picker.prototype._ensureDraft = function () {
    if (!this.draft) {
      var seed = this.value || new Date();
      this.draft = new Date(seed);
      if (!this.opt.time) this.draft.setHours(0, 0, 0, 0);
    }
  };

  Picker.prototype._clampStep = function (unit, dir) {
    this._ensureDraft();
    var d = this.draft, o = this.opt;
    if (unit === 'h') d.setHours(d.getHours() + dir);
    else if (unit === 'm') d.setMinutes(d.getMinutes() + dir * o.minuteStep);
    else if (unit === 's') d.setSeconds(d.getSeconds() + dir);
    this._afterChange();
  };

  Picker.prototype._afterChange = function () {
    this._render();
    if (this.opt.autoApply) this._apply(false);
  };

  Picker.prototype._commitNum = function ($el, normalize) {
    this._ensureDraft();
    var o = this.opt, d = this.draft;
    var unit = $el.data('unit');
    var v = parseInt($el.val(), 10);
    if (isNaN(v)) { if (normalize) this._renderTime(); return; }
    if (unit === 'h') {
      if (o.use24Hour) d.setHours(Math.max(0, Math.min(23, v)));
      else { var pm = d.getHours() >= 12; v = Math.max(1, Math.min(12, v)) % 12; d.setHours(pm ? v + 12 : v); }
    } else if (unit === 'm') d.setMinutes(Math.max(0, Math.min(59, v)));
    else if (unit === 's') d.setSeconds(Math.max(0, Math.min(59, v)));
    if (normalize) this._afterChange();
    // Live per-keystroke updates (raw `input` events) apply the typed value
    // as-is, without clamping to [min, max]: clamping here would rewrite the
    // box out from under the caret on every keystroke (e.g. typing "20" into
    // an hour box floored at 19 becomes unreachable, since the leading "2"
    // gets clamped back to "19" before the "0" is even typed). The clamp
    // still runs on every terminal commit -- blur/change (the `normalize`
    // branch above, via _afterChange), steppers, day clicks, Today, and
    // Apply/Enter -- so nothing out of range can actually be submitted.
    else if (o.autoApply) this._apply(false, true);
  };

  Picker.prototype._apply = function (close, skipClamp) {
    this._ensureDraft();
    var clamped = (this.draft && !skipClamp) ? this._clampToRange(this.draft) : this.draft;
    var wasClamped = !skipClamp && clamped && this.draft && clamped.getTime() !== this.draft.getTime();
    this.draft = clamped;
    this.value = this.draft ? new Date(this.draft) : null;
    var text = this.value ? formatDate(this.value, this.opt.format) : '';
    // Fire a native 'change' too so existing CloudBolt handlers bound to
    // .form-control (e.g. order cost recalc) react like any other field.
    this.$input.val(text).trigger('change.bs5dtp', [this.value]).trigger('change');
    // Reflect the snapped-back value in the open panel (calendar + time
    // steppers), which otherwise still shows whatever was selected/typed
    // before the clamp.
    if (wasClamped && this.open) this._render();
    if (close) this.hide();
  };

  Picker.prototype._bind = function () {
    var self = this, o = this.opt;

    this.$input.on('focus.bs5dtp click.bs5dtp', function () {
      if (self._returningFocus) return;
      self.show();
    });
    this.$input.on('keydown.bs5dtp', function (e) {
      if (e.key === 'Escape') self.hide();
      if (e.key === 'Enter') { self._apply(true); }
    });
    this.$panel.on('keydown.bs5dtp', function (e) {
      if (e.key === 'Escape') self.hide();
      if (e.key === 'Enter' && $(e.target).is('.bs5dtp-num')) self._apply(true);
    });

    // delegate panel interactions
    // keep input focus / stop modal blur close, but let the number inputs receive focus so they can be typed into
    this.$panel.on('mousedown.bs5dtp', function (e) {
      if ($(e.target).is('.bs5dtp-num')) return;
      e.preventDefault();
    });
    this.$panel.on('click.bs5dtp', '.bs5dtp-prev', function () { self.view = new Date(self.view.getFullYear(), self.view.getMonth() - 1, 1); self._renderCalendar(); });
    this.$panel.on('click.bs5dtp', '.bs5dtp-next', function () { self.view = new Date(self.view.getFullYear(), self.view.getMonth() + 1, 1); self._renderCalendar(); });

    this.$panel.on('click.bs5dtp', '.bs5dtp-day:not(.bs5dtp-disabled)', function () {
      var dateStr = $(this).data('date');
      var parts = dateStr.split('-');
      self._ensureDraft();
      self.draft.setFullYear(+parts[0], +parts[1] - 1, +parts[2]);
      if (!o.time) self.draft.setHours(0, 0, 0, 0);
      self.view = new Date(+parts[0], +parts[1] - 1, 1);
      self._render();
      var shouldClose = o.closeOnSelect || (!o.time);
      if (o.autoApply || shouldClose) self._apply(shouldClose);
    });

    // day cells are role="button" divs; Enter/Space must activate them like a real button
    this.$panel.on('keydown.bs5dtp', '.bs5dtp-day:not(.bs5dtp-disabled)', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        $(this).trigger('click');
      }
    });

    this.$panel.on('click.bs5dtp', '.bs5dtp-step', function () {
      self._clampStep($(this).data('unit'), +$(this).data('dir'));
    });

    this.$panel.on('input.bs5dtp', '.bs5dtp-num', function () { self._commitNum($(this), false); });
    this.$panel.on('change.bs5dtp blur.bs5dtp', '.bs5dtp-num', function () { self._commitNum($(this), true); });

    this.$panel.on('click.bs5dtp', '.bs5dtp-ampm', function () {
      self._ensureDraft();
      var d = self.draft;
      d.setHours((d.getHours() + 12) % 24);
      self._afterChange();
    });

    this.$panel.on('click.bs5dtp', '.bs5dtp-today', function () {
      var now = new Date();
      self._ensureDraft();
      self.draft.setFullYear(now.getFullYear(), now.getMonth(), now.getDate());
      if (!o.time) self.draft.setHours(0, 0, 0, 0);
      self.view = new Date(now.getFullYear(), now.getMonth(), 1);
      self._render();
      if (o.autoApply) self._apply(!o.time);
    });

    this.$panel.on('click.bs5dtp', '.bs5dtp-clear', function () {
      self.draft = null; self.value = null;
      self.$input.val('').trigger('change.bs5dtp', [null]).trigger('change');
      self._render();
      self.hide();
    });

    this.$panel.on('click.bs5dtp', '.bs5dtp-apply', function () { self._apply(true); });

    // outside click / scroll / resize
    this._onDocClick = function (e) {
      if (!self.open) return;
      if (self.$input[0] === e.target || self.$input[0].contains(e.target)) return;
      if (self.$panel[0] === e.target || self.$panel[0].contains(e.target)) return;
      self.hide();
    };
    this._onReposition = function () { if (self.open) self._position(); };

    this._onFocusinCapture = function (e) {
      if (self.$panel[0].contains(e.target)) e.stopPropagation();
    };
  };

  Picker.prototype._position = function () {
    var r = this.$input[0].getBoundingClientRect();
    var pw = this.$panel.outerWidth();
    var ph = this.$panel.outerHeight();
    var vw = window.innerWidth, vh = window.innerHeight;
    var top = r.bottom + window.scrollY + 4;
    var left = r.left + window.scrollX;
    // flip up if no room below
    if (r.bottom + ph + 4 > vh && r.top - ph - 4 > 0) top = r.top + window.scrollY - ph - 4;
    // keep within horizontal viewport
    if (left + pw > vw + window.scrollX) left = Math.max(window.scrollX + 4, window.scrollX + vw - pw - 4);
    this.$panel.css({ top: top + 'px', left: left + 'px' });
  };

  Picker.prototype._computeZIndex = function () {
    // sit above any open Bootstrap modal/offcanvas
    var base = 1080;
    $('.modal.show, .offcanvas.show').each(function () {
      var z = parseInt($(this).css('z-index'), 10);
      if (!isNaN(z)) base = Math.max(base, z + 10);
    });
    return base + (this.opt.zIndexBoost || 0);
  };

  Picker.prototype.show = function () {
    if (this.open) return;
    // refresh draft from current input value in case it changed externally
    var typed = parseInput(this.$input.val());
    if (typed) { this.value = new Date(typed); this.draft = new Date(typed); this.view = startOfDay(typed); }
    this.open = true;
    $(this.opt.appendTo).append(this.$panel);
    this.$panel.css({ position: 'absolute', zIndex: this._computeZIndex() }).show();
    this._render();
    this._position();
    $(document).on('mousedown.bs5dtp-' + this.id, this._onDocClick);
    document.addEventListener('focusin', this._onFocusinCapture, true);
    $(window).on('resize.bs5dtp-' + this.id, this._onReposition);
    // Scroll can happen on `window`, on the v5 shell's `.app-shell__main`
    // (CMP-4342), inside a modal, or inside a future scroll container --
    // scroll events do not bubble, so a `$(window).on('scroll')` binding
    // misses every non-document scroll. A capture-phase document listener
    // catches all of them (capture visits every ancestor down from
    // document regardless of bubble), and is a superset of the old
    // window + modal/offcanvas bindings.
    document.addEventListener('scroll', this._onReposition, true);
    this.$input.trigger('show.bs5dtp');
  };

  Picker.prototype.hide = function () {
    if (!this.open) return;
    this.open = false;
    var hadFocus = this.$panel[0].contains(document.activeElement);
    this.$panel.hide().detach();
    if (hadFocus) {
      this._returningFocus = true;
      this.$input.trigger('focus');
      this._returningFocus = false;
    }
    $(document).off('mousedown.bs5dtp-' + this.id);
    document.removeEventListener('focusin', this._onFocusinCapture, true);
    $(window).off('resize.bs5dtp-' + this.id);
    document.removeEventListener('scroll', this._onReposition, true);
    this.$input.trigger('hide.bs5dtp');
  };

  Picker.prototype.getDate = function () { return this.value ? new Date(this.value) : null; };

  Picker.prototype.setDate = function (d) {
    var nd = toDate(d);
    this.value = nd ? new Date(nd) : null;
    this.draft = nd ? new Date(nd) : null;
    if (nd) this.view = startOfDay(nd);
    this.$input.val(nd ? formatDate(nd, this.opt.format) : '');
    if (this.open) this._render();
  };

  Picker.prototype.destroy = function () {
    this.hide();
    this.$input.off('.bs5dtp');
    this.$panel.remove();
    this.$input.removeData('bs5dtp');
  };

  // ---- jQuery bridge --------------------------------------------------------
  $.fn.bs5DateTimePicker = function (arg) {
    var args = Array.prototype.slice.call(arguments, 1);
    var ret;
    this.each(function () {
      var $el = $(this);
      var inst = $el.data('bs5dtp');
      if (!inst) {
        inst = new Picker($el, typeof arg === 'object' ? arg : {});
        $el.data('bs5dtp', inst);
      }
      if (typeof arg === 'string' && typeof inst[arg] === 'function') {
        var r = inst[arg].apply(inst, args);
        if (r !== undefined && ret === undefined) ret = r;
      }
    });
    return ret !== undefined ? ret : this;
  };

  // ---- legacy plugin shims --------------------------------------------------
  // Every existing `.datepicker()` / `.datetimepicker()` call site, like
  // templates and view-injected JS, now uses
  // the new picker with no double-binding. No CloudBolt source uses the
  // method-string API, but we defer to the original plugin if one is passed.
  // Token translation: bootstrap-datepicker / jQuery-UI date tokens are
  // lowercase (yyyy, yy, mm, dd) -> bs5 (YYYY MM DD); moment datetime tokens
  // already use MM/DD/HH/mm/ss, only the year needs casing.
  function dateFmtToBs5(fmt) {
    if (!fmt) return 'YYYY-MM-DD';
    return String(fmt)
      .replace(/yyyy|yy/gi, 'YYYY')
      .replace(/mm/g, 'MM').replace(/dd/gi, 'DD');
  }
  function dateTimeFmtToBs5(fmt) {
    if (!fmt) return 'YYYY-MM-DD HH:mm:ss';
    return String(fmt).replace(/yyyy|yy/g, 'YYYY');
  }
  var _origDatepicker = $.fn.datepicker;
  $.fn.datepicker = function (arg) {
    if (typeof arg === 'string') {
      return _origDatepicker ? _origDatepicker.apply(this, arguments) : this;
    }
    var o = arg || {};
    var fmt = dateFmtToBs5(o.format || o.dateFormat);
    return this.each(function () { $(this).bs5DateTimePicker({ time: false, format: fmt }); });
  };
  $.fn.datepicker.defaults = (_origDatepicker && _origDatepicker.defaults) || {};
  var _origDatetimepicker = $.fn.datetimepicker;
  $.fn.datetimepicker = function (arg) {
    if (typeof arg === 'string') {
      return _origDatetimepicker ? _origDatetimepicker.apply(this, arguments) : this;
    }
    var o = arg || {};
    var fmt = dateTimeFmtToBs5(o.format);
    return this.each(function () {
      $(this).bs5DateTimePicker({ time: true, seconds: /ss/.test(fmt), format: fmt });
    });
  };
  $.fn.datetimepicker.defaults = (_origDatetimepicker && _origDatetimepicker.defaults) || {};

  // ---- declarative auto-init ------------------------------------------------
  // Just add class="bs5datepicker" to any input and it wires itself up.
  // Tune it with data-* attributes (all optional):
  //   data-include-time="true|false"   show the time column (default true)
  //   data-seconds="true|false"        show seconds (default false)
  //   data-24hour="true|false"         24h clock vs 12h AM/PM (default true)
  //   data-format="YYYY-MM-DD HH:mm"   output format (tokens: YYYY MM DD HH hh mm ss A)
  //   data-week-start="0|1"            0 = Sunday, 1 = Monday
  //   data-minute-step="5"             minute increment
  //   data-min-date="2026-01-01"       earliest selectable date ('now' = current moment, live)
  //   data-max-date="2026-12-31"       latest selectable date ('now' = current moment, live)
  //   data-auto-apply="true|false"     apply on each change, no Apply button (default false)
  //   data-close-on-select="true|false"
  //   data-bs5-options='{...}'         JSON for anything else; individual data-* win over it
  // The legacy [data-bs5-datetimepicker] attribute still works too.
  // Also adopt CloudBolt's legacy picker classes so this is a drop-in
  // replacement for the old bootstrap-datepicker / eonasdan-datetimepicker
  // wiring (Django renders date fields with .render_as_datepicker and
  // date-time fields with .render_as_datetimepicker).
  var AUTO_SELECTOR = '.bs5datepicker, [data-bs5-datetimepicker], .render_as_datepicker, .render_as_datetimepicker';

  function readBool(v, def) {
    if (v === undefined || v === null || v === '') return def;
    v = String(v).toLowerCase();
    return v === 'true' || v === '1' || v === 'yes' || v === 'on';
  }
  function readNum(v, def) { var n = parseInt(v, 10); return isNaN(n) ? def : n; }

  function readOptions($el) {
    var opts = {};
    var json = $el.attr('data-bs5-options');
    if (json) { try { opts = JSON.parse(json); } catch (e) {} }

    // Legacy CloudBolt classes set sensible defaults that match the formats the
    // old pickers produced (so server-side parsing is unaffected). Explicit
    // data-* attributes below still override these.
    if ($el.hasClass('render_as_datetimepicker')) {
      opts.time = true; opts.seconds = true; opts.format = 'YYYY-MM-DD HH:mm:ss';
    } else if ($el.hasClass('render_as_datepicker')) {
      opts.time = false; opts.format = 'YYYY-MM-DD';
    }

    if ($el.is('[data-include-time]')) opts.time = readBool($el.attr('data-include-time'), true);
    if ($el.is('[data-bs5-dateonly]')) opts.time = false;
    if ($el.is('[data-seconds]')) opts.seconds = readBool($el.attr('data-seconds'), false);
    if ($el.is('[data-24hour]')) opts.use24Hour = readBool($el.attr('data-24hour'), true);
    if ($el.attr('data-format')) opts.format = $el.attr('data-format');
    if ($el.is('[data-week-start]')) opts.weekStart = readNum($el.attr('data-week-start'), 0);
    if ($el.is('[data-minute-step]')) opts.minuteStep = readNum($el.attr('data-minute-step'), 5);
    if ($el.attr('data-min-date')) opts.minDate = $el.attr('data-min-date');
    if ($el.attr('data-max-date')) opts.maxDate = $el.attr('data-max-date');
    if ($el.is('[data-auto-apply]')) opts.autoApply = readBool($el.attr('data-auto-apply'), true);
    if ($el.is('[data-close-on-select]')) opts.closeOnSelect = readBool($el.attr('data-close-on-select'), false);

    // sensible default formats if the user didn't pin one explicitly
    if (!opts.format) {
      if (opts.time === false) opts.format = 'YYYY-MM-DD';
      else if (opts.use24Hour === false) opts.format = opts.seconds ? 'YYYY-MM-DD hh:mm:ss A' : 'YYYY-MM-DD hh:mm A';
      else if (opts.seconds) opts.format = 'YYYY-MM-DD HH:mm:ss';
    }
    return opts;
  }

  function autoInit(root) {
    var $root = $(root);
    $root.find(AUTO_SELECTOR).addBack(AUTO_SELECTOR).each(function () {
      var $el = $(this);
      if ($el.data('bs5dtp')) return; // already initialized — skip
      $el.bs5DateTimePicker(readOptions($el));
    });
  }
  // expose for manual re-scan after injecting markup
  $.bs5DateTimePicker = { autoInit: autoInit, selector: AUTO_SELECTOR };

  $(function () {
    autoInit(document);
    // wire up inputs added to the DOM later (AJAX-loaded modals, frameworks, etc.)
    if (window.MutationObserver) {
      new window.MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (added[j].nodeType === 1) autoInit(added[j]);
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  });

})(jQuery);
